import { useContext, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../App';
import { GET_MY_PERMISSIONS } from '../graphql/queries/rbac';
import usePermissionsMode from '../components/Permissions/usePermissionsMode';

const DOCUMENT_VISIBILITY_VISIBLE = 'visible';

function useMyPermissions() {
  const { currentOrgId } = useContext(UserContext);
  const { isRbacEnabled } = usePermissionsMode();
  const { key: locationKey } = useLocation();
  const shouldSkip = !isRbacEnabled || !currentOrgId;
  const isInitialRouteRef = useRef(true);

  const { data, loading, error, refetch, networkStatus } = useQuery(GET_MY_PERMISSIONS, {
    skip: shouldSkip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true
  });

  const callerPermissions = shouldSkip ? null : data?.getMyPermissions ?? null;
  const permissions = callerPermissions?.permissions ?? [];
  const roles = callerPermissions?.roles ?? [];
  const hasAccess = shouldSkip || permissions.length > 0;
  const isInitialLoading = !shouldSkip && loading && !data;
  const isRefetching = !shouldSkip && loading && !!data;

  useEffect(() => {
    if (shouldSkip) {
      isInitialRouteRef.current = true;
      return;
    }

    if (isInitialRouteRef.current) {
      isInitialRouteRef.current = false;
      return;
    }

    refetch();
  }, [locationKey, shouldSkip, refetch]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === DOCUMENT_VISIBILITY_VISIBLE && !shouldSkip) {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refetch, shouldSkip]);

  return {
    callerPermissions,
    permissions,
    roles,
    highestPrecedenceRole: callerPermissions?.highestPrecedenceRole ?? null,
    hasAccess,
    isInitialLoading,
    isRefetching,
    error: shouldSkip ? undefined : error,
    refetch,
    networkStatus
  };
}

export default useMyPermissions;
